import React, { useState } from 'react';
import { useImportantDates } from '../../contexts/ImportantDateContext';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Calendar,
  X,
  Check,
  Clock,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';

const ImportantDates = () => {
  const {
    importantDates,
    loading,
    addImportantDate,
    updateImportantDate,
    deleteImportantDate,
    bulkDeleteImportantDates,
  } = useImportantDates();

  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDate, setEditingDate] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');

  // Form state
  const [formData, setFormData] = useState({
    event: '',
    date: '',
    status: 'upcoming',
    description: '',
    order: 0,
    isActive: true,
  });

  // Filter dates
  const filteredDates = importantDates.filter((date) => {
    const matchesSearch =
      date.event.toLowerCase().includes(searchTerm.toLowerCase()) ||
      date.date.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' || date.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  // Stats calculation
  const statsData = {
    total: importantDates.length,
    upcoming: importantDates.filter((d) => d.status === 'upcoming').length,
    ongoing: importantDates.filter((d) => d.status === 'ongoing').length,
    completed: importantDates.filter((d) => d.status === 'completed').length,
  };

  // Modal handlers
  const openModal = (date = null) => {
    if (date) {
      setEditingDate(date);
      setFormData({
        event: date.event,
        date: date.date,
        status: date.status,
        description: date.description || '',
        order: date.order || 0,
        isActive: date.isActive,
      });
    } else {
      setEditingDate(null);
      resetForm();
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingDate(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      event: '',
      date: '',
      status: 'upcoming',
      description: '',
      order: 0,
      isActive: true,
    });
  };

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingDate) {
        await updateImportantDate(editingDate._id, formData);
        alert('Important date updated successfully!');
      } else {
        await addImportantDate(formData);
        alert('Important date created successfully!');
      }
      closeModal();
    } catch (error) {
      alert(error.message || 'Failed to save important date');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this important date?')) {
      try {
        await deleteImportantDate(id);
        alert('Important date deleted successfully');
      } catch (error) {
        alert(error.message || 'Failed to delete important date');
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(`Delete ${selectedDates.length} selected dates?`)) {
      try {
        await bulkDeleteImportantDates(selectedDates);
        setSelectedDates([]);
        alert('Important dates deleted successfully');
      } catch (error) {
        alert(error.message || 'Failed to delete important dates');
      }
    }
  };

  const toggleSelectDate = (id) => {
    setSelectedDates((prev) =>
      prev.includes(id) ? prev.filter((dateId) => dateId !== id) : [...prev, id]
    );
  };

  const selectAllDates = () => {
    if (selectedDates.length === filteredDates.length) {
      setSelectedDates([]);
    } else {
      setSelectedDates(filteredDates.map((d) => d._id));
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'ongoing':
        return <Clock className="w-4 h-4" />;
      case 'upcoming':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Calendar className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-primary-green/10 text-primary-green';
      case 'ongoing':
        return 'bg-primary-yellow/20 text-primary-brown';
      case 'upcoming':
        return 'bg-primary-blue/10 text-primary-blue';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading && importantDates.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-purple mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading important dates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Important Dates</h1>
        <p className="text-gray-600">Manage admission important dates</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Dates</p>
              <p className="text-2xl font-bold text-gray-900">{statsData.total}</p>
            </div>
            <Calendar className="w-8 h-8 text-primary-purple" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Upcoming</p>
              <p className="text-2xl font-bold text-primary-blue">{statsData.upcoming}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-primary-blue" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Ongoing</p>
              <p className="text-2xl font-bold text-primary-yellow">{statsData.ongoing}</p>
            </div>
            <Clock className="w-8 h-8 text-primary-yellow" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-primary-green">{statsData.completed}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-primary-green" />
          </div>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1 flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search dates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="upcoming">Upcoming</option>
              <option value="ongoing">Ongoing</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div className="flex gap-2">
            {selectedDates.length > 0 && (
              <button
                onClick={handleBulkDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete ({selectedDates.length})
              </button>
            )}
            <button
              onClick={() => openModal()}
              className="px-4 py-2 bg-primary-purple text-white rounded-lg hover:bg-primary-purple/90 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Date
            </button>
          </div>
        </div>
      </div>

      {/* Dates Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={
                      selectedDates.length === filteredDates.length && filteredDates.length > 0
                    }
                    onChange={selectAllDates}
                    className="rounded border-gray-300 text-primary-purple focus:ring-primary-purple"
                  />
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Event</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Order</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Active</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredDates.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    No important dates found
                  </td>
                </tr>
              ) : (
                filteredDates.map((date) => (
                  <tr key={date._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedDates.includes(date._id)}
                        onChange={() => toggleSelectDate(date._id)}
                        className="rounded border-gray-300 text-primary-purple focus:ring-primary-purple"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{date.event}</p>
                      {date.description && (
                        <p className="text-sm text-gray-600 line-clamp-1">{date.description}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{date.date}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${getStatusColor(
                          date.status
                        )}`}
                      >
                        {getStatusIcon(date.status)}
                        {date.status.charAt(0).toUpperCase() + date.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{date.order}</td>
                    <td className="px-4 py-3">
                      {date.isActive ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-primary-green/10 text-primary-green">
                          <CheckCircle className="w-3 h-3" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openModal(date)}
                          className="p-2 text-primary-blue hover:bg-primary-blue/10 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(date._id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingDate ? 'Edit Important Date' : 'Add New Important Date'}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Event Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="event"
                    value={formData.event}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                    placeholder="e.g., Admission Form Release"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                    placeholder="e.g., March 1, 2024"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                  >
                    <option value="upcoming">Upcoming</option>
                    <option value="ongoing">Ongoing</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Order
                  </label>
                  <input
                    type="number"
                    name="order"
                    value={formData.order}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                  />
                </div>

                <div className="flex items-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      className="rounded border-gray-300 text-primary-purple focus:ring-primary-purple"
                    />
                    <span className="text-sm font-medium text-gray-700">Active</span>
                  </label>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    maxLength="500"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                    placeholder="Optional description or additional details"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {formData.description.length}/500 characters
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-primary-purple text-white rounded-lg hover:bg-primary-purple/90 transition-colors flex items-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  {editingDate ? 'Update Date' : 'Add Date'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportantDates;
